import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, MapPin, Clock, ArrowUpRight, Instagram, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Reveal } from "@/components/Reveal";
import { WHATSAPP_DISPLAY, PHONE_DISPLAY, PHONE_TEL } from "@/i18n/translations";

const navKeys = ["about", "treatments", "conditions", "journey", "science", "faq"];
const anchorMap = { about: "#about", treatments: "#treatments", conditions: "#conditions", journey: "#method", science: "#science", faq: "/faq" };

const Footer = () => {
  const { t, lang, openBooking, openWhatsApp } = useSite();
  const navigate = useNavigate();
  const [videoFailed, setVideoFailed] = useState(false);
  const f = t.footer;
  const go = (h) => { if (h === "/faq" && lang === "es") { navigate("/es/preguntas-frecuentes"); return; } if (h.startsWith("/")) { navigate(h); return; } const el = document.querySelector(h); if (el) { window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" }); } };

  return (
    <footer id="contact" className="relative bg-navy text-white">
      {/* Contact / final CTA */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-24 sm:pt-32 pb-16">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal>
              <p className="font-serif italic text-2xl sm:text-3xl text-champagne leading-snug max-w-lg">{f.brandLine}</p>
              <p className="font-serif text-white/60 text-xl sm:text-2xl mt-3 max-w-lg">{f.healingLine}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-col sm:flex-row gap-4 mt-9">
                <button onClick={() => openBooking()}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-navy px-8 py-4 font-medium hover:bg-champagne transition-colors"
                  data-testid="footer-book">
                  {t.nav.book} <ArrowUpRight size={17} />
                </button>
                <button onClick={() => openWhatsApp()}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-white px-8 py-4 font-medium hover:bg-white/10 transition-colors"
                  data-testid="footer-wa">
                  <MessageCircle size={18} /> {f.messageWa}
                </button>
              </div>
            </Reveal>

            <div className="mt-12 space-y-5">
              <div className="flex items-start gap-3">
                <MapPin className="text-champagne shrink-0 mt-1" size={18} />
                <div>
                  <p className="font-sans text-white/90">{f.clinic}</p>
                  <p className="font-sans text-white/55 text-sm">{f.location}</p>
                  <p className="font-sans text-white/40 text-xs mt-1 italic">{f.addressPlaceholder}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="text-champagne shrink-0 mt-1" size={18} />
                <div>
                  <p className="font-sans text-white/90">{f.hours}</p>
                  <p className="font-sans text-white/40 text-xs mt-1 italic">{f.hoursPlaceholder}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="text-champagne shrink-0 mt-1" size={18} />
                <a href={PHONE_TEL} className="font-sans text-white/90 hover:text-champagne transition-colors" data-testid="footer-phone">
                  {PHONE_DISPLAY}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MessageCircle className="text-champagne shrink-0 mt-1" size={18} />
                <a href={`https://wa.me/15553039140`} target="_blank" rel="noreferrer"
                  onClick={() => openWhatsApp()} className="font-sans text-white/90 hover:text-champagne transition-colors">
                  {WHATSAPP_DISPLAY}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Instagram className="text-champagne shrink-0 mt-1" size={18} />
                <a href="https://instagram.com/revivereg.mx" target="_blank" rel="noreferrer"
                  className="font-sans text-white/90 hover:text-champagne transition-colors" data-testid="footer-instagram">
                  @Revivereg.mx
                </a>
              </div>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-white/10 h-[340px] lg:h-[420px]">
              <iframe
                title="Revive location — Tijuana"
                src="https://www.google.com/maps?q=Av.%20Santa%20Mar%C3%ADa%2C%20Tijuana%2C%20Baja%20California%2C%20Mexico&output=embed"
                className="w-full h-full grayscale contrast-125 opacity-90"
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.2em] uppercase text-champagne/60 mb-2">{f.directions}</p>
            <h3 className="font-serif text-3xl sm:text-4xl">{f.location}</h3>
            <p className="font-sans text-white/55 mt-3 max-w-md">{f.directionsSub}</p>
            <button onClick={() => openWhatsApp()}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3 font-medium hover:bg-white/10 transition-colors text-sm"
              data-testid="footer-directions-wa">
              <MessageCircle size={16} /> {f.messageWa}
            </button>
          </div>
          <div className="lg:col-span-7">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/30">
              {videoFailed ? (
                <div className="p-8 sm:p-10 text-center" data-testid="directions-fallback">
                  <MapPin className="text-champagne mx-auto mb-4" size={28} />
                  <p className="font-sans text-white/85 text-sm leading-relaxed max-w-md mx-auto">
                    Central Torreo · Av. Santa María, Calete, Int. 402<br />Tijuana, Baja California, México
                  </p>
                  <a href="https://www.google.com/maps?q=Av.%20Santa%20Mar%C3%ADa%2C%20Tijuana%2C%20Baja%20California%2C%20Mexico"
                    target="_blank" rel="noreferrer"
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-champagne/50 text-champagne px-6 py-3 text-sm font-medium hover:bg-champagne/10 transition-colors"
                    data-testid="directions-maps-link">
                    {lang === "es" ? "Cómo llegar (Google Maps)" : "Get Directions (Google Maps)"} <ArrowUpRight size={15} />
                  </a>
                </div>
              ) : (
                <video
                  controls playsInline preload="metadata"
                  className="w-full h-auto max-h-[460px]"
                  data-testid="directions-video"
                  src="/directions.mp4"
                  onError={() => setVideoFailed(true)}
                />
              )}
            </div>
            {!videoFailed && (
              <a href="https://www.google.com/maps?q=Av.%20Santa%20Mar%C3%ADa%2C%20Tijuana%2C%20Baja%20California%2C%20Mexico"
                target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-champagne transition-colors"
                data-testid="directions-maps-link">
                <MapPin size={13} /> {lang === "es" ? "Cómo llegar (Google Maps)" : "Get Directions (Google Maps)"}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Big slogan */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-12">
          <p className="font-serif text-champagne text-center text-2xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="footer-slogan">
            {f.slogan}
          </p>
        </div>
      </div>

      {/* Links */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-12 grid sm:grid-cols-3 gap-10">
          <div>
            <img src="/logo-full.png" alt="Revive Regenerative Medicine" className="h-24 w-auto" />
            <p className="font-sans text-white/45 text-sm mt-4 max-w-xs">{f.clinic} · {f.location}</p>
            <a href="https://instagram.com/revivereg.mx" target="_blank" rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-white/15 pl-1.5 pr-4 py-1.5 hover:border-champagne/50 hover:bg-white/[0.04] transition-colors group"
              data-testid="footer-instagram-btn">
              <span className="grid place-items-center w-8 h-8 rounded-full bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white">
                <Instagram size={17} />
              </span>
              <span className="font-sans text-sm text-white/80 group-hover:text-white transition-colors">@Revivereg.mx</span>
            </a>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-champagne/60 mb-4">{f.quickLinks}</p>
            <ul className="space-y-2">
              {navKeys.map((k) => (
                <li key={k}><button onClick={() => go(anchorMap[k])} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{t.nav[k]}</button></li>
              ))}
              <li><Link to={lang === "es" ? "/es/contacto" : "/contact"} className="font-sans text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-contact-link">{t.nav.contact}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-champagne/60 mb-4">{f.legal}</p>
            <ul className="space-y-2">
              {[[f.privacy, lang === "es" ? "/es/privacidad" : "/privacy"], [f.terms, lang === "es" ? "/es/terminos" : "/terms"], [f.cookies, lang === "es" ? "/es/cookies" : "/cookies"], [f.disclaimerLink, lang === "es" ? "/es/aviso-medico" : "/disclaimer"]].map(([l, to]) => (
                <li key={to}><Link to={to} className="font-sans text-sm text-white/60 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-8">
          <p className="font-sans text-white/40 text-xs leading-relaxed max-w-4xl" data-testid="footer-disclaimer">{f.disclaimer}</p>
          <p className="font-sans text-white/40 text-xs leading-relaxed max-w-4xl mt-4" data-testid="footer-entities">{f.entities}</p>
          <p className="font-sans text-white/30 text-xs mt-4">© {new Date().getFullYear()} {f.clinic}. {f.rights}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
